package com.iregained.site.components.sections.home.sections

import androidx.compose.runtime.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import kotlin.math.pow
import kotlin.math.round

@Composable
private fun Modifier.staggeredFadeInUp(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 40.px).transition(
            Transition.of(
                "opacity",
                duration = 0.8.s,
                timingFunction = TransitionTimingFunction.EaseOut,
                delay = delayMs.ms
            ),
            Transition.of(
                "transform",
                duration = 0.8.s,
                timingFunction = TransitionTimingFunction.EaseOut,
                delay = delayMs.ms
            )
        )
}

@Composable
fun HomeStatsToggleSection(data: HomeStatsToggleSectionData = defaultHomeStatsToggleSectionData) {
    val palette = ColorMode.current.toSitePalette()
    var startAnimation by remember { mutableStateOf(false) }
    var showPrevalence by remember { mutableStateOf(true) }
    val breakpoint = com.iregained.site.utils.LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "home-stats-toggle"

    val headerText = if (showPrevalence) data.prevalenceHeader else data.incidenceHeader
    if (data.stats.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.custom.lightGray)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Column(
                modifier = Modifier.id(targetId).fillMaxWidth().maxWidth(1440.px)
                    .padding(topBottom = if (isDesktop) 50.px else 25.px)
                    .styleModifier { property("scroll-margin-top", "100px") },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Intersection Observer Wrapper
                Div(
                    attrs = Modifier.fillMaxWidth().toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach { entry ->
                                    if (entry.isIntersecting) {
                                        startAnimation = true
                                        obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element)
                            onDispose { observer.disconnect() }
                        }
                    }) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        if (headerText.isNotBlank()) {
                            SectionHeader(
                                text = headerText,
                                fontSize = 32.px,
                                lineWidthPercent = 25.percent,
                                // ANIMATION: Header appears first
                                modifier = Modifier.margin(bottom = 16.px).staggeredFadeInUp(startAnimation, 0),
                                alignment = Alignment.CenterHorizontally
                            )
                        }

                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            // ANIMATION: Toggle section appears second (200ms)
                            modifier = Modifier.margin(topBottom = 25.px).staggeredFadeInUp(startAnimation, 200)
                        ) {
                            SpanText(
                                text = data.toggleLabel,
                                modifier = Modifier.color(Colors.Gray).fontSize(14.px).fontWeight(FontWeight.Bold)
                                    .margin(bottom = 8.px)
                            )

                            Row(
                                modifier = Modifier.backgroundColor(Colors.White).borderRadius(30.px).padding(6.px)
                                    .boxShadow(0.px, 4.px, 15.px, color = Colors.Black.copy(alpha = 15)),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Box(
                                    modifier = Modifier.padding(topBottom = 10.px, leftRight = if (isDesktop) 20.px else 12.px)
                                        .borderRadius(26.px)
                                        .backgroundColor(if (showPrevalence) palette.brand.primary else Colors.Transparent)
                                        .cursor(Cursor.Pointer).onClick { showPrevalence = true },
                                    contentAlignment = Alignment.Center
                                ) {
                                    SpanText(
                                        text = data.prevalenceToggleText,
                                        modifier = Modifier.color(if (showPrevalence) Colors.White else Colors.DimGray)
                                            .fontWeight(FontWeight.Bold)
                                    )
                                }
                                Box(
                                    modifier = Modifier.padding(topBottom = 10.px, leftRight = if (isDesktop) 20.px else 12.px)
                                        .borderRadius(26.px)
                                        .backgroundColor(if (!showPrevalence) palette.brand.primary else Colors.Transparent)
                                        .cursor(Cursor.Pointer).onClick { showPrevalence = false },
                                    contentAlignment = Alignment.Center
                                ) {
                                    SpanText(
                                        text = data.incidenceToggleText,
                                        modifier = Modifier.color(if (!showPrevalence) Colors.White else Colors.DimGray)
                                            .fontWeight(FontWeight.Bold)
                                    )
                                }
                            }
                        }

                        Row(
                            modifier = Modifier.fillMaxWidth().padding(top = 16.px).gap(25.px)
                                .display(DisplayStyle.Flex).flexWrap(FlexWrap.Wrap)
                                .justifyContent(JustifyContent.Center)
                        ) {
                            data.stats.forEachIndexed { index, stat ->
                                key(stat.regionName) {
                                    val currentNumber = if (showPrevalence) stat.prevNumber else stat.incNumber
                                    val currentMetric = if (showPrevalence) stat.prevMetric else stat.incMetric
                                    StatItem(
                                        // ANIMATION: Each stat cascades in one after the other (400ms, 550ms, 700ms...)
                                        modifier = Modifier.flex(1).minWidth(150.px).maxWidth(250.px)
                                            .staggeredFadeInUp(startAnimation, 400 + (index * 150)),
                                        iconPath = stat.iconPath,
                                        number = currentNumber,
                                        metric = currentMetric,
                                        region = stat.regionName,
                                        startAnimation = startAnimation
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StatItem(
    modifier: Modifier, iconPath: String, number: String, metric: String, region: String, startAnimation: Boolean
) {
    val palette = ColorMode.current.toSitePalette()
    val targetValue = number.toFloatOrNull() ?: 0f
    val isDecimal = number.contains(".")
    var animatedValue by remember { mutableStateOf(0f) }

    LaunchedEffect(startAnimation, targetValue) {
        if (startAnimation) {
            val startValue = animatedValue
            val difference = targetValue - startValue
            val durationMs = 1500

            val frameRate = 30

            val totalFrames = (durationMs / 1000f * frameRate).toInt()
            val delayPerFrame = 1000L / frameRate // Now ~33ms delay instead of ~16ms

            for (frame in 0..totalFrames) {
                val progress = frame.toFloat() / totalFrames
                val easedProgress = 1f - (1f - progress).pow(3)
                animatedValue = startValue + (difference * easedProgress)
                delay(delayPerFrame)
            }
            animatedValue = targetValue
        }
    }

    val displayValue = if (isDecimal) {
        val rounded = round(animatedValue * 10).toInt()
        val whole = rounded / 10
        val fraction = rounded % 10
        "$whole.$fraction"
    } else {
        animatedValue.toInt().toString()
    }

    Column(modifier = modifier, horizontalAlignment = Alignment.CenterHorizontally) {
        if (iconPath.isNotBlank()) {
            Image(src = iconPath, modifier = Modifier.unextractable().size(100.px).margin(bottom = 16.px))
        }

        SpanText(
            text = displayValue,
            modifier = Modifier.color(palette.brand.primary).fontSize(48.px).fontWeight(FontWeight.ExtraBold)
                .lineHeight(1.0)
        )

        SpanText(
            text = metric, modifier = Modifier.color(palette.brand.primary).fontSize(24.px).fontWeight(FontWeight.Bold)
        )

        SpanText(
            text = region,
            modifier = Modifier.color(palette.brand.primary).opacity(0.9f).fontSize(22.px).fontWeight(FontWeight.Bold)
                .margin(top = 8.px)
        )
    }
}

/**
 * CMS IMAGE UPLOAD SPECS:
 * @field iconPath:
 * - Aspect Ratio: 1:1 (Perfect Square).
 * - Recommended Dimensions: 150x150px.
 * - Format: SVG (Highly Recommended) or transparent PNG.
 * - Max File Size: 50KB.
 */
data class RegionStatData(
    val regionName: String,
    val iconPath: String,
    val prevNumber: String,
    val prevMetric: String,
    val incNumber: String,
    val incMetric: String
)

data class HomeStatsToggleSectionData(
    val sectionId: String? = null,
    val toggleLabel: String,
    val prevalenceToggleText: String,
    val incidenceToggleText: String,
    val prevalenceHeader: String,
    val incidenceHeader: String,
    val stats: List<RegionStatData>
)

val defaultHomeStatsToggleSectionData = HomeStatsToggleSectionData(
    sectionId = "home-stats-toggle",
    toggleLabel = "SELECT METRIC",
    prevalenceToggleText = "Total Survivors",
    incidenceToggleText = "New Annual Cases",
    prevalenceHeader = "STROKE STATISTICS: CASES & SURVIVORS WITH DISABILITY",
    incidenceHeader = "GLOBAL INCIDENCE RATE: NEW ANNUAL CASES OF STROKE",
    stats = listOf(
        RegionStatData("Worldwide", Res.Images.Home.Stats.ICON_WORLD, "94", "Million", "15", "Million"),
        RegionStatData("Canada", Res.Images.Home.Stats.ICON_CANADA, "800", "Thousand", "100", "Thousand"),
        RegionStatData("USA", Res.Images.Home.Stats.ICON_USA, "8", "Million", "800", "Thousand"),
        RegionStatData("India", Res.Images.Home.Stats.ICON_INDIA, "6.5", "Million", "1.5", "Million"),
        RegionStatData("Europe", Res.Images.Home.Stats.ICON_EU, "12", "Million", "1.5", "Million")
    )
)